<script setup>
import { computed, watch } from 'vue';
import {
  useHead,
  useRoute,
  useAsyncData,
  createError,
  navigateTo,
  useRequestURL,
} from '#imports';
import { useCmsScripts } from '@/composables/useCmsScripts';
import { scheduleRecaptchaPreload } from '@/composables/useRecaptcha';
import { fetchSettingsWithRetry } from '@/composables/useSettingsFetch';
import { getCachedDataWithServerPrefetch } from '@/composables/useServerPrefetchCache';
import { useGlobalStore } from '@/stores/global';
import AppHeader from '@/components/layout/Header/Header.vue';
import AppFooter from '@/components/layout/Footer/Footer.vue';
import ApplicationPopup from '@/components/ApplicationPopup.vue';
import ModalSupport from '@/components/modals/ModalSupport.vue';
const { ssrContext } = useNuxtApp();
let host = '';

if (process.server) {
  host = ssrContext?.event?.node?.req?.headers?.host || '';
} else {
  host = window.location.host;
}
defineProps({
  className: {
    type: String,
    default: undefined,
  },
  errorData: {
    type: Object,
    default: () => null,
  },
});
const route = useRoute();

const isThanksPage = computed(
  () => route.name === 'thanks' || route.path.includes('/thanks'),
);

const globalStore = useGlobalStore();

const {
  data: apiSettings,
  status,
} = await useAsyncData('globalSettings', () => fetchSettingsWithRetry(), {
  server: true,
  getCachedData: getCachedDataWithServerPrefetch,
});

const handleSettingsRedirect = (data) => {
  if (data?.redirect?.redirect_url) {
    const { redirect_url, redirect_status } = data.redirect;
    const currentPath = route.fullPath;
    const isSamePage =
      redirect_url === currentPath ||
      redirect_url === `${useRequestURL().origin}${currentPath}`;

    if (!isSamePage) {
      return navigateTo(redirect_url, {
        redirectCode: parseInt(redirect_status) || 301,
        external: redirect_url.startsWith('http'),
      });
    } else {
      console.warn('Recursive redirect detected and blocked:', redirect_url);
    }
  }
};

watch(
  apiSettings,
  (newData) => {
    if (newData) handleSettingsRedirect(newData);
  },
  { immediate: true },
);

useCmsScripts(
  computed(() => apiSettings.value?.scriptsHead),
  computed(() => apiSettings.value?.startScriptsBody),
  computed(() => apiSettings.value?.endScriptsBody),
);
useHead(
  computed(() => {
    const backupLogo = apiSettings.value?.header?.logo?.src || '/favicon.svg';

    const links = [
      {
        rel: 'icon',
        type: 'image/x-icon',
        sizes: (apiSettings.value?.favicon && apiSettings.value?.favicon.height && apiSettings.value?.favicon.width) ? `${apiSettings.value?.favicon.height}x${apiSettings.value?.favicon.width}` : '32x32',
        href:
          (apiSettings.value?.favicon && apiSettings.value?.favicon.src) ||
          backupLogo ||
          '/favicon.ico',
      },
      {
        rel: 'icon',
        type: 'image/png',
        sizes: (apiSettings.value?.favicon_32 && apiSettings.value?.favicon_32.height && apiSettings.value?.favicon_32.width) ? `${apiSettings.value?.favicon_32.height}x${apiSettings.value?.favicon_32.width}` : '32x32',
        href:
          (apiSettings.value?.favicon_32 && apiSettings.value?.favicon_32.src) || backupLogo || '/favicon-32x32.png',
      },
      {
        rel: 'apple-touch-icon',
        sizes: (apiSettings.value?.favicon_apple && apiSettings.value?.favicon_apple.height && apiSettings.value?.favicon_apple.width) ? `${apiSettings.value?.favicon_apple.height}x${apiSettings.value?.favicon_apple.width}` : '180x180',
        href:
          (apiSettings.value?.favicon_apple && apiSettings.value?.favicon_apple.src) ||
          backupLogo ||
          '/apple-touch-icon.png',
      },
    ];

    return {
      link: links,
    };
  }),
);

watch(
  () => apiSettings.value?.google_captcha_site_key,
  (siteKey) => {
    if (process.client && siteKey) scheduleRecaptchaPreload(siteKey);
  },
  { immediate: true },
);
</script>

<template>
  <div
    v-if="apiSettings"
    class="layouts-default"
    :class="[className, { 'is-error-page': !!errorData }]"
  >
    <AppHeader :data="apiSettings?.header" :socials="apiSettings?.socials" />

    <main class="layouts-default__view">
      <template v-if="errorData">
        <section
          class="section section--top-xl section--bottom-xl bg-gray black"
        >
          <div class="container max-w-784">
            <div class="error">
              <h1 class="mb-0 h1">{{ errorData.statusCode }}</h1>
              <p class="mb-0 body-1-regular">
                {{
                  errorData.statusCode === 404
                    ? $t('error.page_doesnt_exist')
                    : errorData.message || $t('error.something_went_wrong')
                }}
              </p>
            </div>
          </div>
        </section>
      </template>
      <template v-else>
        <slot />
      </template>
    </main>

    <AppFooter :data="apiSettings?.footer" :socials="apiSettings?.socials" />
    <ApplicationPopup />
    <ModalSupport v-if="!isThanksPage" :data="apiSettings?.support" />
  </div>
  <div v-else class="layouts-default is-error-page">
    <main class="layouts-default__view">
      <section class="section section--top-xl section--bottom-xl bg-gray black">
        <div class="container max-w-784">
          <div class="error">
            <h1 class="mb-0 h1">500</h1>
            <p class="mb-0 body-1-regular">
              {{ $t('error.something_went_wrong') || 'Failed to load app settings' }}
            </p>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>
<style scoped lang="scss">
.layouts-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.layouts-default {
  display: flex;
  flex-direction: column;
  flex-grow: 1;

  &__view {
    flex-grow: 1;
  }
}

.layouts-error {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}
.error {
  align-items: center;
  display: flex;
  gap: 24px;
  flex-direction: column;
  text-align: center;
  @include media-breakpoint-up(sm) {
    gap: 32px;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 32);
  }
}
</style>
